import React, { useState } from 'react';
import { Table } from './pilot-ui/table';
import { Link } from 'react-router';
import PressureChart from './PressureChart';
import RuntimeChart from './RuntimeChart';
import RuntimeHeatCoolChart from './RuntimeHeatCoolChart';
import ReferenceDataSection from './ReferenceDataSection';
import { AnalysisResults, ChartSeries, CompareOrientation, PinnedAnalysis, DeviceEnrichmentMap, DeviceEnrichment } from '../types';
import CumulativeRuntimeChart from './CumulativeRuntimeChart';
import TempHumidityChart from './TempHumidityChart';
import ReturnDeltasChart from './ReturnDeltasChart';
import FanDeltasChart from './FanDeltasChart';
import ScatterPlotChart from './ScatterPlotChart';
import TemperaturePlotChart from './TemperaturePlotChart';
import { AnalysisTooltipSuppressedContext } from './AnalysisTooltip';
import {
  AnalysisAirflow,
  SystemAirflowCurrentConfiguration,
  SystemAirflowCurrentProvider,
} from './SystemAirflowCurrent';
import NoteImageModal from './NoteImageModal';
import FilterLifeCalculationPopover from './FilterLifeCalculationPopover';
import { ChartCsvContext, ChartCsvScope } from './ChartCsvButton';
import { SeriesNotes } from './NoteMarkerOverlay';
import { useDevicesNotes } from '../hooks/useDeviceNotes';
import { useAuth } from '../context/AuthContext';
import {
  computeFilterLifeDisplay,
  replacementStatusFromAlerts,
} from '../utils/filterLifeDisplay';
import { calendarDaysSince, formatCalendarDate } from '../utils/calendarDate';
import { daysBetweenDateInputs, todayUtcDateInput } from '../utils/dateRange';

interface PressureAnalysisDisplayProps {
  analyses: PinnedAnalysis[];
  startDate?: string;
  endDate?: string;
  isFilterPeriod?: boolean;
  orientation?: CompareOrientation;
  // Firestore enrichment keyed by device name; supplies the app-facing
  // airflowPercentageFloor shown next to our pipeline's airflow %.
  enrichmentMap?: DeviceEnrichmentMap;
}

// FS-488: every date this labels — install_date, chart_data.dates, the
// requested window, the predicted change date — is a bare YYYY-MM-DD calendar
// date. It used to go through new Date(), which reads that as midnight UTC and
// named the previous day in every US zone.
const formatDate = formatCalendarDate;

// Plan 80: pins are identified by (device.id, analysisKey) so the same device
// can appear twice under two analyses without colliding React keys / series ids.
const pinKey = (a: PinnedAnalysis): string => `${a.device.id}:${a.analysisKey}`;

/**
 * FS-508: does the response actually name a filter?
 *
 * Selecting a period whose filter was never recorded — everything older than
 * the first recorded change, or a change logged without its filter — comes
 * back with no filter identity ON PURPOSE: the filter in the device today was
 * installed later, so naming it would be a false statement. The block then has
 * nothing to render, which reads as a page that failed to load, so it says so
 * instead.
 */
export const hasFilterIdentity = (filterInfo: AnalysisResults['filter_info']): boolean =>
  filterInfo != null && (
    filterInfo.manufacturer != null
    || filterInfo.rating != null
    || filterInfo.length != null
    || filterInfo.width != null
    || filterInfo.thickness != null
  );

const DeviceHeader: React.FC<{ analysis: PinnedAnalysis }> = ({ analysis }) => (
  <div className="flex items-center gap-2 mb-2">
    <span
      className="inline-block w-3 h-3 rounded-full"
      style={{ backgroundColor: analysis.color }}
      aria-hidden
    />
    <span className="text-sm font-semibold text-gray-700">
      {analysis.device.description || analysis.device.name}
    </span>
    {analysis.device.description && (
      <span className="text-xs text-gray-600 font-mono">{analysis.device.name}</span>
    )}
    {analysis.analysisKey !== 'standard_daily_v1' && (
      <span className="text-xs text-indigo-600 font-medium">· {analysis.analysisLabel}</span>
    )}
  </div>
);

const AnalysisStatsBlock: React.FC<{
  analysis: PinnedAnalysis;
  startDate?: string;
  endDate?: string;
  enrichment?: DeviceEnrichment;
}> = ({ analysis, startDate, endDate, enrichment }) => {
  const data = analysis.results;
  // Filter life exactly as the homeowner's app shows it — driven by the
  // Firestore airflowPercentageFloor through the app's own display logic
  // (Randy 2026-07-12: customer-visible surfaces must never contradict the
  // app, so no raw pipeline %, no stale filter.status, and no change date
  // until the floor reaches the replacement threshold).
  const rawThickness = enrichment?.filter?.thickness ?? data.filter_info?.thickness;
  const thickness = rawThickness != null && !Number.isNaN(Number(rawThickness))
    ? Number(rawThickness)
    : null;
  const replacementStatus = replacementStatusFromAlerts(enrichment?.filterAlerts);
  const filterLife = computeFilterLifeDisplay(
    enrichment?.device?.airflowPercentageFloor,
    thickness,
    enrichment?.filter?.predictedChangeDate,
    replacementStatus,
  );
  // FS-727: that floor is the CURRENT filter's and knows nothing of periods.
  // On a previous period it would read as the old filter's own life, which it
  // never was. A hand-typed range (no period) keeps the app's number.
  const isPreviousPeriod = !!data.filter_period && !data.filter_period.is_current;
  // FS-1131: the inputs behind the number, for the eye beside it.
  const calculation = isPreviousPeriod ? null : data.filter_life_calculation ?? null;
  const calculationAnalysisName = data.available_analyses
    ?.find((a) => a.analysis_key === calculation?.analysis_key)?.display_name ?? null;
  return (
    <div className="flex gap-16 flex-wrap">
      <div className="min-w-[200px]">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Filter Info</h3>
        <Table>
          <tbody className="divide-y divide-gray-200">
            {/* FS-516: date the filter this block is NAMING. FS-508 re-points
                the identity below at the selected Analysis Period, but
                install_date is the devices row — the filter in the device
                today — so a previous period was stamped with the install day
                of a filter fitted after it ended, and its own change date
                appeared nowhere. Before the first recorded change we know
                neither the filter nor the day it went in, so the row goes
                away rather than borrow a later filter's date. */}
            {(() => {
              const period = data.filter_period ?? null;
              const installed = period && !period.is_current
                ? period.effective_date
                : data.install_date;
              if (!installed) return null;
              return (
                <tr>
                  <td className="py-1 pr-4 text-sm text-muted-foreground">Installed</td>
                  <td className="py-1 text-sm font-medium">{formatDate(installed)}</td>
                </tr>
              );
            })()}
            {data.filter_info?.manufacturer && (
              <tr>
                <td className="py-1 pr-4 text-sm text-muted-foreground">Manufacturer</td>
                <td className="py-1 text-sm font-medium">
                  {/* FS-594: the named filter links to its catalog performance
                      data. Plenty of filters never match a catalog row (and no
                      response carries the id until the analysis lambda ships),
                      so an absent id renders the same plain text as before —
                      never a link that goes nowhere. */}
                  {data.filter_info.filter_catalog_id != null ? (
                    <Link
                      to={`/filter-catalog?filter=${data.filter_info.filter_catalog_id}`}
                      className="text-link hover:underline"
                      title="Filter performance data"
                    >
                      {data.filter_info.manufacturer}
                    </Link>
                  ) : (
                    data.filter_info.manufacturer
                  )}
                </td>
              </tr>
            )}
            {data.filter_info?.rating && (
              <tr>
                <td className="py-1 pr-4 text-sm text-muted-foreground">Rating</td>
                <td className="py-1 text-sm font-medium">{data.filter_info.rating}</td>
              </tr>
            )}
            {(data.filter_info?.length != null && data.filter_info?.width != null) && (
              <tr>
                <td className="py-1 pr-4 text-sm text-muted-foreground">Dimensions</td>
                <td className="py-1 text-sm font-medium">
                  {data.filter_info.length} × {data.filter_info.width}
                  {data.filter_info.thickness != null && ` × ${data.filter_info.thickness}`} in
                </td>
              </tr>
            )}
            {data.filter_info?.thickness && (
              <tr>
                <td className="py-1 pr-4 text-sm text-muted-foreground">Thickness</td>
                <td className="py-1 text-sm font-medium">{data.filter_info.thickness}</td>
              </tr>
            )}
            {!hasFilterIdentity(data.filter_info) && (
              <tr>
                <td className="py-1 pr-4 text-sm text-muted-foreground">Filter</td>
                <td className="py-1 text-sm font-medium text-muted-foreground">Not recorded</td>
              </tr>
            )}
          </tbody>
        </Table>
        {/* FS-593: the system's current filter configuration, under the
            filter this block names. Read once per device with the estimate
            in the Airflow section (SystemAirflowCurrentProvider). */}
        <SystemAirflowCurrentConfiguration deviceId={analysis.device.id} />
      </div>

      <div className="overflow-x-auto">
        <h2 className="text-xl font-semibold mb-1">Analysis Results</h2>
        {startDate && endDate && (() => {
          // FS-607: count from the current filter's start when we have it,
          // and only call it a filter change when that is what it is.
          // filter_start_date is null after every change until the nightly
          // re-establishes the baseline, and on any device that has never
          // had one \u2014 counting from install_date and labelling it a filter
          // change reported device 285's filter as 95 days old against a
          // baseline dated 58 days earlier.
          //
          // FS-608: both of those dates come from the devices row, so they
          // describe the CURRENT filter whichever period is being shown. On a
          // PREVIOUS period the response carries that period's own change
          // date, and the count has to stop at the end of the window rather
          // than run on to today. The current period, and any range with no
          // period selected, keep the FS-607 behaviour exactly.
          const period = data.filter_period ?? null;
          const day = 1000 * 60 * 60 * 24;
          const windowDays =
            Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / day) + 1;

          if (period && !period.is_current) {
            // No effective date means the period predates the first recorded
            // change (FS-508 pre-history). The filter is unknown, so its age
            // is too \u2014 the device's own dates belong to a filter installed
            // later and must not stand in for it.
            const changedOn = period.effective_date;
            if (!changedOn) {
              return <p className="text-sm text-muted-foreground mb-2">{`Over the last ${windowDays} days`}</p>;
            }
            const periodDays = Math.max(0, Math.round(
              (new Date(endDate).getTime() - new Date(changedOn).getTime()) / day));
            return (
              <p className="text-sm text-muted-foreground mb-2">
                {`Filter age at the end of this period \u2014 ${periodDays} days`}
              </p>
            );
          }

          // FS-607 round 2: the CURRENT filter's anchor is resolved on the
          // server, so this page and the phone card cannot disagree about
          // what the count means. Device 221 read 126 days here against 49
          // there while each worked it out from the raw dates.
          const resolved = data.filter_anchor;
          if (resolved?.date && resolved.kind) {
            const days = calendarDaysSince(resolved.date);
            if (days !== null) {
              const label = resolved.kind === 'filter_change'
                ? `Since filter was changed \u2014 ${days} days`
                : resolved.kind === 'install'
                  ? `Since installation \u2014 ${days} days`
                  // The Clip was put on a filter that was already in the
                  // furnace: we know when we started measuring, not how old
                  // that filter is.
                  : `Measuring for ${days} days`;
              return <p className="text-sm text-muted-foreground mb-2">{label}</p>;
            }
          }

          // A response from a Lambda predating filter_anchor means what it
          // always meant — same date precedence, same labels. The COUNT is
          // whole UTC days even here: rounding elapsed milliseconds put the
          // web a day ahead of the card after noon UTC.
          const anchor = data.filter_start_date ?? data.install_date;
          const days = calendarDaysSince(anchor) ?? windowDays;
          const label = data.filter_start_date
            ? `Since filter was changed \u2014 ${days} days`
            : data.install_date
              ? `Since installation \u2014 ${days} days`
              : `Over the last ${days} days`;
          return <p className="text-sm text-muted-foreground mb-2">{label}</p>;
        })()}
        <Table>
          <tbody className="divide-y divide-gray-200">
            <tr className="hover:bg-gray-50">
              <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm flex items-center">
                <span className="mr-2">📊</span>
                <span className="text-muted-foreground">Current Pressure ({(() => {
                  const dates = data.chart_data.dates;
                  const pressures = data.chart_data.pressures;
                  for (let i = pressures.length - 1; i >= 0; i--) {
                    if (pressures[i] != null) return formatDate(dates[i]);
                  }
                  return 'N/A';
                })()})</span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm text-right font-medium">
                {data.current_pressure?.toFixed(2)} Pa
              </td>
            </tr>
            <tr className="hover:bg-gray-50">
              <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm flex items-center">
                <span className="mr-2">📈</span>
                <span className="text-muted-foreground">Daily Change (last 30d trend)</span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm text-right font-medium">
                {data.chart_data.trend_line.rate_unavailable
                  ? <span className="text-gray-600 font-normal">Not enough data yet</span>
                  : `${data.chart_data.trend_line.slope?.toFixed(3)} Pa/day`}
              </td>
            </tr>
            <tr className="hover:bg-gray-50">
              <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm flex items-center">
                <span className="mr-2">R</span>
                <span className="text-muted-foreground">Total Runtime ({startDate && endDate ? `${formatDate(startDate)} \u2013 ${formatDate(endDate)}` : ''})</span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm text-right font-medium">
                {(data.total_runtime / 60).toFixed(1)} hrs
              </td>
            </tr>
            <tr className="hover:bg-gray-50">
              <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm flex items-center">
                <span className="mr-2">M</span>
                <span className="text-muted-foreground">Average Daily Runtime (last {data.avg_daily_runtime_30d_days ?? 'N/A'}d)</span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm text-right font-medium">
                {((data.avg_daily_runtime_30d ?? data.total_average_daily_runtime) / 60).toFixed(1)} hrs/day
              </td>
            </tr>
            {enrichment?.device && (
              <tr className="hover:bg-gray-50">
                <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm flex items-center">
                  <span className="mr-2">💨</span>
                  <span className="text-muted-foreground">Filter Life</span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-right font-medium">
                  {isPreviousPeriod && (
                    <span className="font-normal text-gray-600">Filter life is shown for the current filter only</span>
                  )}
                  {!isPreviousPeriod && filterLife.kind === 'expect_reading' && (
                    <span className="italic text-gray-600">Expect reading in 24&ndash;48 hrs</span>
                  )}
                  {!isPreviousPeriod && filterLife.kind === 'change_now' && (
                    <span className="font-bold text-status-error-foreground">Change now</span>
                  )}
                  {!isPreviousPeriod && filterLife.kind === 'change_by' && (
                    <span className="font-semibold text-status-warning-foreground">Change by {formatDate(filterLife.date)}</span>
                  )}
                  {!isPreviousPeriod && filterLife.kind === 'change_soon' && (
                    <span className="font-semibold text-status-warning-foreground">Change soon</span>
                  )}
                  {!isPreviousPeriod && filterLife.kind === 'life_pct' && (
                    <span className={filterLife.percent < 10 ? 'text-status-error-foreground' : filterLife.percent <= 25 ? 'text-status-warning-foreground' : ''}>
                      {filterLife.percent}%
                    </span>
                  )}
                  {calculation && (
                    <span className="ml-1 align-middle">
                      <FilterLifeCalculationPopover
                        calculation={calculation}
                        floor={enrichment.device.airflowPercentageFloor}
                        analysisName={calculationAnalysisName}
                        thickness={thickness}
                        predictedChangeDate={enrichment.filter?.predictedChangeDate}
                        replacementStatus={replacementStatus}
                      />
                    </span>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

const RuntimeFooterTable: React.FC<{ analysis: PinnedAnalysis }> = ({ analysis }) => {
  const data = analysis.results;
  return (
    <div className="max-w-2xl overflow-x-auto">
      <Table>
        <tbody className="divide-y divide-gray-200">
          <tr className="hover:bg-gray-50">
            <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm flex items-center">
              <span className="mr-2">📈</span>
              <span className="text-muted-foreground">Hourly Change (last 30d trend)</span>
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-sm text-right font-medium">
              {data.chart_data.runtime_trend_line.slope?.toFixed(4)} Pa/hr
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

// Both raw charts refuse a range over 400 days (ScatterPlotChart,
// TemperaturePlotChart).
const RAW_PLOT_MAX_DAYS = 400;

/**
 * FS-723: the raw charts' own end date — the scatter's and, since 2026-09-28,
 * the temperature plot's. The page's range stops at the last processed day
 * because the daily charts can go no further; the raw charts read raw
 * readings and can. So a range that ends ON the last processed day — the
 * default range, or the current filter period — runs on to today (a UTC day,
 * as both plot lambdas bound their queries), and `analysedThrough` tells the
 * charts where the analysis stops. A previous period, a range ending earlier,
 * an unknown window, or a stretch the 400-day guard would refuse keep the
 * range's own end.
 */
export const rawPlotRangeEnd = (
  analysis: PinnedAnalysis,
  startDate: string,
  endDate: string,
  now: Date = new Date(),
): { endDate: string; analysedThrough?: string } => {
  const latest = analysis.latestProcessedDate;
  const period = analysis.results.filter_period ?? null;
  if (!latest || endDate !== latest) return { endDate };
  if (period && !period.is_current) return { endDate };
  const today = todayUtcDateInput(now);
  if (today <= endDate) return { endDate };
  const span = daysBetweenDateInputs(startDate, today);
  if (span === null || span > RAW_PLOT_MAX_DAYS) return { endDate };
  return { endDate: today, analysedThrough: latest };
};

// Shared Y-axis domain across all pinned analyses, spanning both return and
// fan deltas so the two charts are directly comparable per device.
const computeDeltaYDomain = (analyses: PinnedAnalysis[]): [number, number] | undefined => {
  const all: number[] = [];
  for (const a of analyses) {
    const rd = a.results.chart_data?.return_deltas;
    if (rd) {
      const vals = Array.isArray(rd) ? rd : (rd.transitions || []).map((t: any) => t.delta);
      vals.forEach((v: number | null) => {
        if (v !== null && Math.abs(v) >= 10) all.push(v);
      });
    }
    const fd = a.results.chart_data?.fan_deltas;
    if (fd) {
      fd.forEach((v: number | null) => {
        if (v !== null && Math.abs(v) >= 10) all.push(v);
      });
    }
  }
  return all.length > 0 ? [Math.min(...all), Math.max(...all)] : undefined;
};

// FS-955: product decision 2026-10-19 — the banner needs a 30-day slope below this.
const NEGATIVE_TREND_BANNER_SLOPE_PA_PER_DAY = -0.1;

// FS-955: a flat filter's slope wobbles either side of zero (device 123 sits at
// -0.002 Pa/day), so only a real fall trips the banner. A period with no rate
// (FS-579) carries a placeholder slope, which says nothing about a fall.
const hasFallingPressureTrend = (analysis: PinnedAnalysis): boolean => {
  const trendLine = analysis.results.chart_data.trend_line;
  return !trendLine.rate_unavailable && trendLine.slope < NEGATIVE_TREND_BANNER_SLOPE_PA_PER_DAY;
};

const PressureAnalysisDisplay: React.FC<PressureAnalysisDisplayProps> = ({
  analyses,
  startDate,
  endDate,
  orientation = 'vertical',
  enrichmentMap,
}) => {
  const { hasPermission } = useAuth();
  const showAdvanced = hasPermission('advanced_analysis');
  const notesByDeviceId = useDevicesNotes(analyses.map((a) => a.device.id));
  const [noteImageNoteId, setNoteImageNoteId] = useState<number | null>(null);

  // Chart markers only carry the note id; resolve the note's attachment ids
  // for the gallery modal from the already-fetched notes.
  const noteImageIdsFor = (noteId: number): number[] => {
    for (const list of Object.values(notesByDeviceId)) {
      const note = list.find((n) => n.id === noteId);
      if (note) return (note.images ?? []).map((img) => img.id);
    }
    return [];
  };

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    analyses.forEach((a) => {
      const count = (notesByDeviceId[a.device.id] || []).length;
      const rng = startDate && endDate ? ` range=${startDate}..${endDate}` : '';
      console.debug(
        `[PressureAnalysisDisplay] notes for ${a.device.name} (id=${a.device.id}): ${count}${rng}`,
      );
    });
  }

  if (analyses.length === 0) return null;

  const isMulti = analyses.length > 1;
  const isOverlay = isMulti && orientation === 'overlaid';

  // Horizontal only applies when multi-device AND orientation is horizontal.
  // Overlaid falls back to a column layout for per-device sections
  // (stats blocks, charts that don't overlay cleanly).
  const containerClass = isMulti
    ? orientation === 'horizontal'
      ? 'flex flex-wrap gap-4'
      : 'flex flex-col gap-6'
    : '';
  const itemClass = isMulti && orientation === 'horizontal'
    ? analyses.length === 2
      ? 'w-[calc(50%-0.5rem)] min-w-[400px]'
      : 'w-[calc(33.333%-0.67rem)] min-w-[400px]'
    : 'w-full';

  // Per-device series array, used by overlay-capable charts.
  const series: ChartSeries[] = analyses.map((a) => ({
    id: pinKey(a),
    label: a.analysisKey !== 'standard_daily_v1'
      ? `${a.device.description || a.device.name} · ${a.analysisLabel}`
      : (a.device.description || a.device.name),
    color: a.color,
    data: a.results.chart_data,
  }));

  // One raw-chart range per pin, computed once so the scatter and the
  // temperature plot always agree on where they end.
  const rawRanges = new Map(
    startDate && endDate
      ? analyses.map((a) => [pinKey(a), rawPlotRangeEnd(a, startDate, endDate)] as const)
      : [],
  );

  // Names each chart's CSV download: the device (and its analysis when it is
  // not the standard one) or the compared set, plus the requested window.
  const csvScope = (subject: string): ChartCsvScope => ({ subject, startDate, endDate });
  const pinSubject = (a: PinnedAnalysis): string =>
    a.analysisKey !== 'standard_daily_v1' ? `${a.device.name}-${a.analysisKey}` : a.device.name;

  // Sections with per-device content arranged by orientation (vertical/horizontal stack).
  const renderSection = (
    sectionTitle: string | null,
    render: (a: PinnedAnalysis) => React.ReactNode,
    filter?: (a: PinnedAnalysis) => boolean,
  ) => {
    const visible = filter ? analyses.filter(filter) : analyses;
    if (visible.length === 0) return null;
    return (
      <div className="mt-8">
        {sectionTitle && isMulti && (
          <h3 className="text-sm font-semibold text-gray-600 mb-2 border-b pb-1">
            {sectionTitle}
          </h3>
        )}
        <div className={containerClass}>
          {visible.map((a) => {
            const node = render(a);
            if (!node) return null;
            return (
              <div key={pinKey(a)} className={itemClass}>
                {isMulti && <DeviceHeader analysis={a} />}
                <ChartCsvContext.Provider value={csvScope(pinSubject(a))}>
                  {node}
                </ChartCsvContext.Provider>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Overlay-capable sections: in overlay mode, render one chart with the full
  // series array. In other modes, fall back to the per-device renderer.
  const renderOverlayableSection = (
    sectionTitle: string,
    perDevice: (a: PinnedAnalysis) => React.ReactNode,
    overlay: (series: ChartSeries[]) => React.ReactNode,
    filter?: (a: PinnedAnalysis) => boolean,
  ) => {
    if (!isOverlay) {
      return renderSection(sectionTitle, perDevice, filter);
    }
    const visible = filter ? analyses.filter(filter) : analyses;
    if (visible.length === 0) return null;
    const visibleSeries = series.filter((_, i) => !filter || filter(analyses[i]));
    const node = overlay(visibleSeries);
    if (!node) return null;
    return (
      <div className="mt-8">
        <h3 className="text-sm font-semibold text-gray-600 mb-2 border-b pb-1">
          {sectionTitle}
        </h3>
        <ChartCsvContext.Provider
          value={csvScope(visible.map(pinSubject).join('-vs-'))}
        >
          {node}
        </ChartCsvContext.Provider>
      </div>
    );
  };

  const deltaYDomain = computeDeltaYDomain(analyses);
  const anyFallingPressureTrend = analyses.some(hasFallingPressureTrend);

  const noteSeriesFor = (a: PinnedAnalysis): SeriesNotes[] => {
    const notes = notesByDeviceId[a.device.id] || [];
    return notes.length > 0
      ? [{ deviceId: a.device.id, color: a.color, notes }]
      : [];
  };

  const allNoteSeries: SeriesNotes[] = analyses
    .map((a) => ({
      deviceId: a.device.id,
      color: a.color,
      notes: notesByDeviceId[a.device.id] || [],
    }))
    .filter((sn) => sn.notes.length > 0);

  const page = (
    <div className="mt-2">
      <div className="bg-card border border-border rounded-lg shadow-md p-3">
        {anyFallingPressureTrend && (
          <div className="mt-4 mb-6 border-2 border-red-600 rounded-lg p-4 bg-red-100">
            <p className="text-red-800 font-semibold text-lg">Negative pressure trend detected</p>
          </div>
        )}

        {renderSection('Summary', (a) => (
          <AnalysisStatsBlock analysis={a} startDate={startDate} endDate={endDate} enrichment={enrichmentMap?.[a.device.name]} />
        ))}

        {renderOverlayableSection(
          'Pressure',
          (a) => a.results.chart_data && (
            <PressureChart
              jsonData={a.results.chart_data}
              noteSeries={noteSeriesFor(a)}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
          (s) => (
            <PressureChart
              jsonData={s[0].data}
              overlaySeries={s}
              noteSeries={allNoteSeries}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
        )}

        {/* FS-748: one airflow value per filter period, from the Clip's
            clean days, replaces the daily airflow chart. Not gated on daily
            CFM: a Clip with none still says why. */}
        {renderSection('Airflow', (a) => (
          <AnalysisAirflow
            deviceId={a.device.id}
            pastPeriod={!!a.results.filter_period && !a.results.filter_period.is_current}
            periodAirflow={a.results.period_airflow}
            showHeading={!isMulti}
          />
        ))}

        {renderOverlayableSection(
          'Return-side Pressure Transition Deltas',
          (a) => {
            const cd = a.results.chart_data;
            if (!cd?.return_deltas) return null;
            return (
              <ReturnDeltasChart
                jsonData={cd}
                yDomain={deltaYDomain}
                noteSeries={noteSeriesFor(a)}
                onNoteImageClick={setNoteImageNoteId}
              />
            );
          },
          (s) => (
            <ReturnDeltasChart
              jsonData={s[0].data}
              overlaySeries={s}
              noteSeries={allNoteSeries}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
          (a) => !!a.results.chart_data?.return_deltas,
        )}

        {showAdvanced && renderOverlayableSection(
          'Fan Pressure Transition Deltas',
          (a) => {
            const cd = a.results.chart_data;
            if (!cd?.fan_deltas) return null;
            return (
              <FanDeltasChart
                jsonData={cd}
                yDomain={deltaYDomain}
                noteSeries={noteSeriesFor(a)}
                onNoteImageClick={setNoteImageNoteId}
              />
            );
          },
          (s) => (
            <FanDeltasChart
              jsonData={s[0].data}
              overlaySeries={s}
              noteSeries={allNoteSeries}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
          (a) => !!a.results.chart_data?.fan_deltas,
        )}

        {renderSection('Runtime Trend', (a) => <RuntimeFooterTable analysis={a} />)}

        {renderSection(
          'Cumulative Runtime',
          (a) => a.results.chart_data && <CumulativeRuntimeChart jsonData={a.results.chart_data} />,
        )}

        {renderOverlayableSection(
          'Temperature and Humidity',
          (a) => a.results.chart_data && (
            <TempHumidityChart
              jsonData={a.results.chart_data}
              noteSeries={noteSeriesFor(a)}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
          (s) => (
            <TempHumidityChart
              jsonData={s[0].data}
              overlaySeries={s}
              noteSeries={allNoteSeries}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
        )}

        {renderOverlayableSection(
          'Runtime',
          (a) => a.results.chart_data && (
            <RuntimeChart
              jsonData={a.results.chart_data}
              noteSeries={noteSeriesFor(a)}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
          (s) => (
            <RuntimeChart
              jsonData={s[0].data}
              overlaySeries={s}
              noteSeries={allNoteSeries}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
        )}

        {showAdvanced && renderSection(
          'Runtime (heat / cool)',
          (a) => a.results.chart_data && (
            <RuntimeHeatCoolChart
              deviceName={a.device.name}
              jsonData={a.results.chart_data}
              noteSeries={noteSeriesFor(a)}
              onNoteImageClick={setNoteImageNoteId}
            />
          ),
        )}

        {startDate && endDate && renderSection(
          null,
          (a) => <ReferenceDataSection
            deviceName={a.device.name}
            startDate={startDate}
            endDate={endDate}
            referenceError={a.results.chart_data.reference_error}
          />,
          (a) => {
            const sources = a.results.chart_data.reference_sources;
            return sources === null
              ? !!a.results.chart_data.reference_error
              : Array.isArray(sources) && sources.length > 0;
          },
        )}

        {startDate && endDate && renderSection(
          'Scatter Plot',
          (a) => {
            const raw = rawRanges.get(pinKey(a)) ?? { endDate };
            return (
              <ScatterPlotChart
                deviceId={a.device.id.toString()}
                startDate={startDate}
                endDate={raw.endDate}
                analysedThrough={raw.analysedThrough}
                notes={notesByDeviceId[a.device.id] || []}
                noteColor={a.color}
                onNoteImageClick={setNoteImageNoteId}
              />
            );
          },
        )}

        {startDate && endDate && renderSection(
          'Temperature Plot',
          (a) => {
            const raw = rawRanges.get(pinKey(a)) ?? { endDate };
            return (
              <TemperaturePlotChart
                deviceId={a.device.id.toString()}
                startDate={startDate}
                endDate={raw.endDate}
                analysedThrough={raw.analysedThrough}
                notes={notesByDeviceId[a.device.id] || []}
                noteColor={a.color}
                onNoteImageClick={setNoteImageNoteId}
              />
            );
          },
        )}

        {startDate && endDate && renderSection(
          'Humidity Plot',
          (a) => {
            const raw = rawRanges.get(pinKey(a)) ?? { endDate };
            return (
              <TemperaturePlotChart
                metric="humidity"
                deviceId={a.device.id.toString()}
                startDate={startDate}
                endDate={raw.endDate}
                analysedThrough={raw.analysedThrough}
                notes={notesByDeviceId[a.device.id] || []}
                noteColor={a.color}
                onNoteImageClick={setNoteImageNoteId}
              />
            );
          },
        )}
      </div>
      {noteImageNoteId != null && (
        <NoteImageModal
          noteId={noteImageNoteId}
          imageIds={noteImageIdsFor(noteImageNoteId)}
          alt="Note attachment"
          onClose={() => setNoteImageNoteId(null)}
        />
      )}
    </div>
  );

  // FS-593: one system-airflow read per device, shared by the filter
  // configuration under Filter Info and the estimate in the Airflow section.
  return (
    <SystemAirflowCurrentProvider deviceIds={analyses.map((a) => a.device.id)}>
      <AnalysisTooltipSuppressedContext.Provider value={noteImageNoteId !== null}>
        {page}
      </AnalysisTooltipSuppressedContext.Provider>
    </SystemAirflowCurrentProvider>
  );
};

export default PressureAnalysisDisplay;
